import { type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/buttons/actionButtonVariants.ts';

interface ActionButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Blokuje przycisk i zamienia treść na kręcący się znak. */
  isLoading?: boolean;
}

/**
 * Jedyny przycisk aplikacji — stopki modali, nagłówki sekcji, wiersze tabel,
 * kroki wprowadzania paczki, ekrany publiczne.
 *
 * Zastąpił `CacButton`; skala pochodzi z handoffu (promień 9 px, tekst 15 px,
 * bursztyn `accent-600`), a ładowanie chowa treść zamiast ją wygaszać, więc
 * przycisk nie zmienia szerokości w trakcie zapisu.
 */
const ActionButton = ({
  tone,
  size,
  iconOnly,
  isLoading = false,
  type = 'button',
  disabled = false,
  className,
  children,
  ...props
}: ActionButtonProps) => (
  <button
    type={type}
    disabled={disabled || isLoading}
    aria-busy={isLoading || undefined}
    // Ładowanie to nie wyłączenie: wygaszony przycisk z bladym znakiem czyta
    // się na telefonie jako „nie reaguje”, więc kolor zostaje pełny.
    className={cn(
      buttonVariants({ tone, size, iconOnly }),
      isLoading && 'disabled:cursor-wait disabled:opacity-100',
      className,
    )}
    {...props}
  >
    {/*
     * `contents` zostawia dzieci bezpośrednimi elementami przycisku, więc
     * układ z `flex-1` u konsumenta działa dalej, a `invisible` dziedziczą
     * i znikają na czas ładowania, nie zmieniając szerokości przycisku.
     */}
    <span className={cn('contents', isLoading && 'invisible')}>{children}</span>

    {isLoading && (
      <span className="absolute inset-0 flex items-center justify-center">
        <LoaderCircle aria-hidden className="animate-spin" />
      </span>
    )}
  </button>
);

export default ActionButton;
